import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, map, Observable, switchMap } from 'rxjs';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { GenericFormGenService } from 'src/app/page_component_item/generic-form-gen/service/generic-form-gen.service';
import { ciInterface } from 'src/app/util/interfaces/ci';
import { ciTypesInterface } from 'src/app/util/interfaces/ciTypes';
import { genericFormInterface } from 'src/app/util/interfaces/genericForm';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root',
})
export class CmdbSearchFilterService {
	private getAvailableFiltersByTypeUrl = environment.baseApiUrl + 'cmdb/search/field?{queryString}';
	private getAllCiTypesUrl = environment.baseApiUrl + 'cmdb/citype';
	private getCiListUrl = environment.baseApiUrl + 'cmdb/search?{queryString}';

	constructor(
		private AuthLoginService: AuthLoginService,
		private http: HttpClient,
		private Labels: LabelsService,
		private GenericFormGenService: GenericFormGenService
	) {}

	initialType = '';
	fieldDictionary = {};

	getAvailableFilters(idCustomer: string, allowedCiTypes: number[], idCiType?: string): Observable<genericFormInterface[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		if (!idCiType) {
			idCiType = '0';
		}
		this.initialType = idCiType;
		let getAvailableFiltersByTypeUrl = this.getAvailableFiltersByTypeUrl.replace(
			'{queryString}',
			`idCiType=${idCiType}&idCustomer=${idCustomer}`
		);
		getAvailableFiltersByTypeUrl += `&isAnalystView=${localStorage.getItem('sessionStatus') === '1'}`;
		return this.Labels.getLabels().pipe(
			switchMap((labels) => {
				return this.http.get<any[]>(this.getAllCiTypesUrl, { headers: headers }).pipe(
					switchMap((ciTypes) => {
						return this.http.get<any[]>(getAvailableFiltersByTypeUrl, { headers: headers }).pipe(
							map((filtersRes) => {
								const newCmdbFilterArray: genericFormInterface[] = [];

								for (const filter of filtersRes) {
									let newCmdbFilter: genericFormInterface;
									if (filter.ciType === '1A' && filter.name === 'type') {
										newCmdbFilter = {
											id: filter.name,
											name: filter.name,
											inputType: 'select',
											label: filter.label,
											subSelect: filter.subSelect,
											values: [''],
											items: [],
											outItems: [],
											commonGroup: filter.ciType,
										};

										for (const ciType of ciTypes) {
											if (allowedCiTypes.length > 0 && !allowedCiTypes.includes(ciType.id)) continue;

											const ciTypeItems: genericFormInterface['items'][number] = {
												label: ciType.label,
												name: ciType.type,
												values: [ciType.id],
											};
											newCmdbFilter.items.push(ciTypeItems);
										}
									} else {
										newCmdbFilter = {
											id: filter.name,
											name: filter.name,
											inputType: filter.inputType,
											label: filter.label,
											hidden: filter.hidden,
											subSelect: filter.subSelect,
											values: [''],
											items: [],
											outItems: [],
											commonGroup: filter.ciType,
										};

										if (filter.items && filter.items.length > 0) {
											for (const item of filter.items) {
												const newItem: genericFormInterface = {
													name: item.name,
													values: [item.value],
													label: item.label,
												};

												newCmdbFilter.items.push(newItem);
											}
										}

										newCmdbFilter = this.formatFilters(newCmdbFilter);
									}

									if (newCmdbFilter.items && newCmdbFilter.items.length > 1) {
										newCmdbFilter.items.sort((a, b) => {
											if (labels[a.label] > labels[b.label]) return 1;
											if (labels[a.label] < labels[b.label]) return -1;
											return 0;
										});
									}

									this.fieldDictionary[newCmdbFilter.name] = newCmdbFilter;
									newCmdbFilterArray.push(newCmdbFilter);
								}

								this.loadSubSelect();

								return newCmdbFilterArray;
							})
						);
					})
				);
			})
		);
	}

	getCiList(idCustomer: string, allowedCiTypes: number[], ciFilters?: genericFormInterface[]): Observable<ciInterface> {
		let queryString = ``;
		if (ciFilters) {
			for (const ciFilter of ciFilters) {
				if (ciFilter.values && ciFilter.values.length > 0) {
					queryString += `&${ciFilter.name}=${ciFilter.operator ? ciFilter.operator : ''}`;
					for (const [index, value] of ciFilter.values.entries()) {
						if (value != '') {
							queryString += `${value}${index == ciFilter.values.length - 1 ? '' : '§'}`;
						}
					}
				}
			}
		}
		queryString += `&idCustomer=${idCustomer}&allowedCiTypes=${allowedCiTypes.join(',')}`;
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		let getCiListUrl = this.getCiListUrl.replace('{queryString}', queryString);
		getCiListUrl += `&isAnalystView=${localStorage.getItem('sessionStatus') === '1'}`;
		return this.Labels.getLabels().pipe(
			switchMap((labels) => {
				return this.http.get<any[]>(this.getAllCiTypesUrl, { headers: headers }).pipe(
					switchMap((ciTypes) => {
						return this.http.get<JSON[]>(getCiListUrl, { headers: headers }).pipe(
							map((ciListRes: any) => {
								const newCiList: ciInterface = {
									ciArray: [],
									ciType: 'MULTIPLE',
									ciIcon: '',
								};

								ciListRes.forEach((ciRes: any) => {
									const newCi: ciInterface['ciArray'][number] = {
										id: ciRes.id,
										cmdb_id: ciRes.cmdb_id,
										identification: ciRes.identification,
										status: ciRes.status,
										type: ciRes.type,
										idCiType: ciRes.type,
									};

									for (const ciType of ciTypes) {
										if (ciType.id === newCi.type) {
											newCi.type = labels[ciType.label] ? labels[ciType.label] : ciType.label;
										}
									}

									newCiList.ciArray.push(newCi);
								});

								newCiList.ciArray.sort((a, b) => {
									if (a.identification.toString().toLocaleLowerCase() > b.identification.toString().toLocaleLowerCase())
										return 1;
									if (a.identification.toString().toLocaleLowerCase() < b.identification.toString().toLocaleLowerCase())
										return -1;
									return 0;
								});

								newCiList.ciArray.sort((a, b) => {
									if (a.type.toString().toLocaleLowerCase() > b.type.toString().toLocaleLowerCase()) return 1;
									if (a.type.toString().toLocaleLowerCase() < b.type.toString().toLocaleLowerCase()) return -1;
									return 0;
								});

								return newCiList;
							})
						);
					})
				);
			})
		);
	}

	getCiType(idCiType: string): Observable<ciTypesInterface> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.get<JSON[]>(this.getAllCiTypesUrl, { headers: headers }).pipe(
			map((ciTypes) => {
				let newCiType: ciTypesInterface = {
					id: '',
					name: '',
					type: '',
					label: '',
				};

				ciTypes.forEach((ciType: any) => {
					if (ciType.id.toString() === idCiType) {
						newCiType = {
							id: ciType.id,
							name: ciType.type,
							type: ciType.type,
							label: ciType.label,
						};

						return newCiType;
					}
				});

				return newCiType;
			})
		);
	}

	formatFilters(newCmdbFilter: genericFormInterface): genericFormInterface {
		if (newCmdbFilter.inputType === 'boolean') {
			const newItemTrue: genericFormInterface = {
				name: 'true',
				values: [1],
				label: 'lbl_geral_sim',
			};

			const newItemFalse: genericFormInterface = {
				name: 'false',
				values: [0],
				label: 'lbl_geral_nao',
			};

			newCmdbFilter.items.push(newItemTrue, newItemFalse);
		}

		return newCmdbFilter;
	}

	loadSubSelect(): void {
		const fieldKeyArray = Object.keys(this.fieldDictionary);
		const parameter = [{ key: 'type', value: this.initialType }];
		for (const index in fieldKeyArray) {
			const field: genericFormInterface = this.fieldDictionary[fieldKeyArray[index]];
			if (!field || !field.subSelect || field.subSelect.length === 0) continue;

			for (const subSelect of field.subSelect) {
				this.fieldDictionary[subSelect.subSelectChildName].inputType = 'select';
				this.GenericFormGenService.getSubSelect(subSelect.subSelectQryName, 'all', parameter).subscribe((subSelectItems) => {
					this.fieldDictionary[subSelect.subSelectChildName].items = subSelectItems;
				});
			}
		}
	}
}
